import { createTheme, ThemeOptions } from '@mui/material/styles';
import { tokens } from './tokens';

/**
 * Dark Theme Configuration
 * 
 * Uses design tokens with appropriate color inversions for dark mode.
 * Light backgrounds become dark, dark text becomes light.
 */
export const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: tokens.colors.secondary[400], // Use teal for dark mode primary
      light: tokens.colors.secondary[300],
      dark: tokens.colors.secondary[600],
      contrastText: '#FFFFFF', // Changed from neutral[900] for better contrast
    },
    secondary: {
      main: tokens.colors.primary[400], // Use lighter navy for dark mode secondary
      light: tokens.colors.primary[300],
      dark: tokens.colors.primary[600],
      contrastText: '#FFFFFF', // Changed from neutral[900] for better contrast
    },
    success: {
      main: tokens.colors.success.light,
      light: '#A5D6A7',
      dark: tokens.colors.success.main, // Use main instead of dark for better contrast
      contrastText: '#FFFFFF', // Changed from neutral[900] for better contrast
    },
    warning: {
      main: tokens.colors.warning.light,
      light: '#FFCC80',
      dark: tokens.colors.warning.main, // Use main instead of dark for better contrast
      contrastText: '#000000', // Keep black for warning
    },
    error: {
      main: tokens.colors.error.light,
      light: '#EF5350',
      dark: tokens.colors.error.main, // Use main instead of dark for better contrast
      contrastText: '#FFFFFF', // Changed from neutral[900] for better contrast
    },
    info: {
      main: tokens.colors.info.light,
      light: '#90CAF9',
      dark: tokens.colors.info.main, // Use main instead of dark for better contrast
      contrastText: '#FFFFFF', // Changed from neutral[900] for better contrast
    },
    background: {
      default: '#0F172A', // Slate 900 - Industrial dark canvas
      paper: '#1E293B', // Slate 800 - Industrial elevated surface
    },
    text: {
      primary: '#F8FAFC', // Slate 50
      secondary: '#94A3B8', // Slate 400
      disabled: '#64748B', // Slate 500
    },
    divider: '#334155', // Slate 700
    action: {
      active: tokens.colors.neutral[300],
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(66, 165, 245, 0.16)',
      disabled: tokens.colors.neutral[600],
      disabledBackground: tokens.colors.neutral[800],
    },
  },
  typography: {
    fontFamily: tokens.typography.fontFamily.primary,
    fontSize: 16,
    fontWeightLight: 300,
    fontWeightRegular: tokens.typography.fontWeight.regular,
    fontWeightMedium: tokens.typography.fontWeight.medium,
    fontWeightBold: tokens.typography.fontWeight.bold,
    h1: {
      fontSize: tokens.typography.fontSize['4xl'],
      fontWeight: tokens.typography.fontWeight.bold,
      lineHeight: tokens.typography.lineHeight.tight,
    },
    h2: {
      fontSize: tokens.typography.fontSize['3xl'],
      fontWeight: tokens.typography.fontWeight.bold,
      lineHeight: tokens.typography.lineHeight.tight,
    },
    h3: {
      fontSize: tokens.typography.fontSize['2xl'],
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.tight,
    },
    h4: {
      fontSize: tokens.typography.fontSize.xl,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    h5: {
      fontSize: tokens.typography.fontSize.lg,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    h6: {
      fontSize: tokens.typography.fontSize.md,
      fontWeight: tokens.typography.fontWeight.semibold,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    body1: {
      fontSize: tokens.typography.fontSize.md,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    body2: {
      fontSize: tokens.typography.fontSize.sm,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    caption: {
      fontSize: tokens.typography.fontSize.xs,
      lineHeight: tokens.typography.lineHeight.normal,
    },
    button: {
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: tokens.typography.fontWeight.medium,
      textTransform: 'none',
    },
  },
  spacing: tokens.spacing.unit,
  shape: {
    borderRadius: parseInt(tokens.borderRadius.md),
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.6)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.6)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.6)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Global focus indicator styles for accessibility
        '*:focus-visible': {
          outline: `3px solid ${tokens.colors.primary[400]}`,
          outlineOffset: '2px',
        },
        // Ensure all interactive elements are keyboard accessible
        'a, button, input, select, textarea, [role="button"], [role="link"], [tabindex]:not([tabindex="-1"])': {
          '&:focus-visible': {
            outline: `3px solid ${tokens.colors.primary[400]}`,
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.colors.neutral[800],
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.colors.neutral[800],
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          margin: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
          '&.Mui-selected': {
            backgroundColor: 'rgba(66, 165, 245, 0.16)',
            '&:hover': {
              backgroundColor: 'rgba(66, 165, 245, 0.24)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          textTransform: 'none',
          fontWeight: tokens.typography.fontWeight.semibold,
          letterSpacing: '0.01em',
          padding: `${tokens.spacing.sm}px ${tokens.spacing.md}px`,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #26B7C3 0%, #1F929C 100%)',
          color: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(38, 183, 195, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #2ED0DE 0%, #26B7C3 100%)',
            boxShadow: '0 4px 16px rgba(38, 183, 195, 0.45)',
            transform: 'translateY(-1px)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #3E3B5D 0%, #32304A 100%)',
          color: '#FFFFFF',
          border: '1px solid #4D4A70',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #4D4A70 0%, #3E3B5D 100%)',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.4)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: '#334155',
          color: '#F8FAFC',
          '&:hover': {
            borderColor: '#26B7C3',
            backgroundColor: 'rgba(38, 183, 195, 0.08)',
            color: '#26B7C3',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(56, 189, 248, 0.08)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.md,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&:active': {
            transform: 'scale(0.96)',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E293B',
          border: '1px solid #334155',
          borderRadius: 10,
          boxShadow: '0 12px 28px -4px rgba(0, 0, 0, 0.6), 0 6px 12px -4px rgba(0, 0, 0, 0.4)',
          padding: '4px 0',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '2px 6px',
          padding: '8px 12px',
          fontSize: '0.875rem',
          fontWeight: 500,
          color: '#F8FAFC',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(56, 189, 248, 0.12)',
            color: '#38BDF8',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(38, 183, 195, 0.18)',
            color: '#26B7C3',
            '&:hover': {
              backgroundColor: 'rgba(38, 183, 195, 0.25)',
            },
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E293B',
          border: '1px solid #334155',
          borderRadius: 12,
          boxShadow: '0 16px 32px -4px rgba(0, 0, 0, 0.6)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0F172A',
          border: '1px solid #334155',
          borderRadius: 6,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '6px 10px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        },
        arrow: {
          color: '#0F172A',
          '&:before': {
            border: '1px solid #334155',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.neutral[800],
            '& fieldset': {
              borderColor: tokens.colors.neutral[700],
            },
            '&:hover fieldset': {
              borderColor: tokens.colors.neutral[600],
            },
            '&.Mui-focused fieldset': {
              borderColor: tokens.colors.primary[400],
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.borderRadius.lg,
          backgroundColor: '#1E293B',
          border: '1px solid #334155',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
          backgroundImage: 'none',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E293B',
          borderColor: '#334155',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#0F172A',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover, &.MuiTableRow-hover:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(38, 183, 195, 0.16) !important',
            '&:hover': {
              backgroundColor: 'rgba(38, 183, 195, 0.24) !important',
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#334155',
          color: '#F8FAFC',
        },
        head: {
          backgroundColor: '#0F172A',
          color: '#94A3B8',
          fontWeight: 600,
          borderColor: '#334155',
        },
        body: {
          color: '#F8FAFC',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: '#94A3B8',
          borderColor: '#334155',
        },
        selectIcon: {
          color: '#94A3B8',
        },
        actions: {
          color: '#94A3B8',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        outlined: {
          borderColor: '#334155',
        },
      },
    },
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: tokens.zIndex.sticky,
    drawer: tokens.zIndex.dropdown,
    modal: tokens.zIndex.modal,
    snackbar: tokens.zIndex.toast,
    tooltip: tokens.zIndex.tooltip,
  },
};

export const darkTheme = createTheme(darkThemeOptions);
